/**
 * Conversion detail for each service page: exactly what the client receives,
 * how quickly, an honest price range, and a sample output they can picture.
 *
 * Kept separate from `services-data.ts` so commercial copy (prices, turnaround)
 * can be updated without touching the editorial service descriptions.
 */

export type ServiceConversion = {
  /** Concrete artefacts handed over at the end of the engagement. */
  deliverables: string[];
  /** Plain-language turnaround promise. */
  turnaround: string;
  /** Indicative price band in ZAR — always framed as "from". */
  pricingHint: string;
  /** A short description of a real, representative output. */
  sampleOutput: string;
};

const DEFAULT_CONVERSION: ServiceConversion = {
  deliverables: [
    "Written summary of your position and the applicable law",
    "All drafted documents in Word and PDF",
    "A short action plan with dates and next steps",
  ],
  turnaround: "First response within 24 hours; standard work delivered in 3–5 working days.",
  pricingHint: "From R750 per matter — fixed quote confirmed before any work starts.",
  sampleOutput: "A structured document pack with a covering note explaining how to use each item.",
};

export const SERVICE_CONVERSION: Record<string, ServiceConversion> = {
  "labour-relations": {
    deliverables: [
      "Procedure map for your matter (LRA / BCEA / Code of Good Practice)",
      "Hearing pack: charge sheet review, evidence bundle index, witness statements",
      "Opening statement and cross-examination question set",
      "Written legal opinion on the strength of the case (non-reserved matters)",
    ],
    turnaround: "Urgent hearing prep in 48 hours; full packs in 3–5 working days.",
    pricingHint: "From R950 for hearing preparation; policy work quoted per document.",
    sampleOutput:
      "A 12-page disciplinary hearing pack: numbered evidence bundle, three witness statements, opening address and a question schedule per charge.",
  },
  "ccma-support": {
    deliverables: [
      "Completed LRA 7.11 referral form ready for filing",
      "Chronology of events with supporting document index",
      "Conciliation and arbitration preparation notes",
      "Settlement range analysis and negotiation script",
    ],
    turnaround: "Referral documents within 48 hours (we work to your 30-day deadline).",
    pricingHint: "From R850 for a referral pack; arbitration preparation from R1 500.",
    sampleOutput:
      "A referral bundle: LRA 7.11 form, one-page chronology, indexed annexures and a two-page arbitration brief.",
  },
  "legal-drafting": {
    deliverables: [
      "Final agreement in Word and PDF, clause-numbered and signature-ready",
      "Plain-language clause guide explaining each key term",
      "One round of revisions included",
    ],
    turnaround: "Standard agreements in 2–3 working days; complex drafting in 5–7.",
    pricingHint: "From R650 per agreement; bundles discounted.",
    sampleOutput:
      "A service-level agreement with defined terms, payment and termination clauses, a POPIA clause and a signature page with initialling blocks.",
  },
  compliance: {
    deliverables: [
      "Compliance gap assessment against POPIA / B-BBEE / sector rules",
      "Prioritised remediation register with owners and due dates",
      "Template policies, notices and consent forms",
    ],
    turnaround: "Assessment report in 5 working days; policy suite in 7–10.",
    pricingHint: "From R2 500 for a small-business POPIA readiness pack.",
    sampleOutput:
      "A POPIA readiness report scoring 12 control areas, plus a privacy notice, PAIA manual outline and data-subject request procedure.",
  },
  "debt-assistance": {
    deliverables: [
      "Letter of demand or response letter, correctly formulated",
      "Debt position statement with prescription analysis",
      "Payment-arrangement proposal and negotiation script",
    ],
    turnaround: "Letters drafted within 24–48 hours.",
    pricingHint: "From R450 per letter; full negotiation support from R1 200.",
    sampleOutput:
      "A letter of demand citing the agreement, amount, interest and the NCA position, with a payment proposal annexure.",
  },
  "court-online": {
    deliverables: [
      "CaseLines / Court Online profile set-up and bundle upload",
      "Correctly paginated and indexed digital bundle",
      "Filing confirmation and reference log",
    ],
    turnaround: "Bundles uploaded and indexed within 48 hours of receiving documents.",
    pricingHint: "From R550 per bundle, depending on page count.",
    sampleOutput:
      "A hyperlinked, paginated CaseLines bundle with a section index and a filing confirmation sheet.",
  },
  "case-management": {
    deliverables: [
      "Matter file structure with a document register",
      "Deadline and diary schedule for the whole matter",
      "Monthly progress report",
    ],
    turnaround: "File set up within 3 working days; ongoing management is monthly.",
    pricingHint: "From R1 500 per month per matter portfolio.",
    sampleOutput:
      "A matter dashboard listing every deadline, document, and next action with responsible-party columns.",
  },
  "public-speaking": {
    deliverables: [
      "Tailored talk or workshop (45–90 minutes)",
      "Slide deck and handout for attendees",
      "Q&A session and follow-up resource list",
    ],
    turnaround: "Bookings confirmed within 24 hours; content prepared 5 working days before the date.",
    pricingHint: "From R2 000 per session; schools and NPOs at reduced rates.",
    sampleOutput:
      "A 60-minute workshop on workplace rights with a 20-slide deck and a one-page rights summary handout.",
  },
  "drafting-paralegal": {
    deliverables: [
      "Drafted affidavits, notices and correspondence",
      "Research memo with authorities on your issue",
      "Document checklist for filing or submission",
    ],
    turnaround: "Most documents in 2–4 working days.",
    pricingHint: "From R500 per document; research memos from R900.",
    sampleOutput:
      "A founding affidavit skeleton with numbered paragraphs, annexure references and a supporting research memo.",
  },
  "company-secretarial": {
    deliverables: [
      "CIPC filings prepared and lodged (annual returns, director changes)",
      "Statutory registers and minute book templates",
      "Board and shareholder resolution pack",
    ],
    turnaround: "CIPC filings lodged within 48 hours of receiving FICA documents.",
    pricingHint: "From R450 per filing; annual retainer from R3 500.",
    sampleOutput:
      "A company file with an updated director register, signed resolutions and a CIPC filing confirmation.",
  },
};

export function getServiceConversion(slug: string): ServiceConversion {
  return SERVICE_CONVERSION[slug] ?? DEFAULT_CONVERSION;
}
